import type { ComposerSendOptions } from '@/types/composer';
import { isCsvFile } from '@/utils/csvAttachment';
import { parseAccessGroupPrefillFromMessage } from '@/utils/parseAccessGroupMessage';
import { parseDeviceSetupPrefillFromMessage } from '@/utils/parseDeviceSetupMessage';
import {
  isUserCreationMessage,
  parseUserCreationPrefillFromMessage,
} from '@/utils/createUserCommand';
import { isBulkUserImportMessage } from '@/utils/parseBulkUserImportMessage';
import type { UserIntentResolution } from './types';

const DEFAULT_CSV_MESSAGE = 'Import users from the attached file.';

export function resolveUserIntent(
  rawMessage: string,
  options?: ComposerSendOptions
): UserIntentResolution | null {
  const message = rawMessage.trim();
  const attachment = options?.attachment ?? null;
  const hasAttachment = Boolean(attachment && isCsvFile(attachment));

  if (!message && !hasAttachment) return null;

  if (hasAttachment) {
    return {
      kind: 'flow',
      flowId: 'bulk-user-import',
      message: message || DEFAULT_CSV_MESSAGE,
      hasAttachment: true,
    };
  }

  if (isBulkUserImportMessage(message)) {
    return {
      kind: 'flow',
      flowId: 'bulk-user-import',
      message,
      hasAttachment: false,
    };
  }

  const isUserRequest = isUserCreationMessage(message);
  const isAccessGroupRequest =
    /add|create|new|setup/i.test(message) && /access\s*group/i.test(message);
  const isDeviceRequest =
    /add|create|setup|install|set\s+up/i.test(message) &&
    /controller|device|panel/i.test(message);

  if (isUserRequest) {
    const prefill = parseUserCreationPrefillFromMessage(message);
    return {
      kind: 'flow',
      flowId: 'user-creation',
      message,
      prefill: prefill ?? undefined,
      hasAttachment: false,
    };
  }

  if (isAccessGroupRequest) {
    const prefill = parseAccessGroupPrefillFromMessage(message);
    return {
      kind: 'flow',
      flowId: 'access-group-creation',
      message,
      prefill: prefill ?? undefined,
      hasAttachment: false,
    };
  }

  if (isDeviceRequest) {
    const prefill = parseDeviceSetupPrefillFromMessage(message);
    return {
      kind: 'flow',
      flowId: 'device-setup',
      message,
      prefill: prefill ?? undefined,
      hasAttachment: false,
    };
  }

  return { kind: 'conversation', message, hasAttachment: false };
}
